import classNames from 'classnames';
import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  HistoryListImages,
} from '@bingads-webui-campaign-react/ads-studio-shared-components';
import { Icon, IconButton } from '@bingads-webui-react/fabric-control';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { getGenerationHistoryListStyles } from '../../styles/generation-history-list-styles';

// the image width is 90px
const useHistoryListState = ({ imageWidth = 90, imagesRef, allImageCount }) => {
  const [firstImageIndex, setFirstImageIndex] = useState(0); // Shift the first image index to show the images
  const containerWidth = imagesRef ? imagesRef.clientWidth : 368;

  const showedImageCount = Math.floor(containerWidth / imageWidth);
  const shouldShowLeftArrow = firstImageIndex > 0;
  const shouldShowRightArrow = firstImageIndex + showedImageCount < allImageCount;
  const shiftLeft = useCallback(() => {
    setFirstImageIndex(firstImageIndex - 1 < 0 ? 0 : firstImageIndex - 1);
  }, [firstImageIndex]);

  const shiftRight = useCallback(() => {
    const newFirstImageIndex = firstImageIndex + 1;
    if (newFirstImageIndex + showedImageCount <= allImageCount) {
      setFirstImageIndex(newFirstImageIndex);
    }
  }, [firstImageIndex, showedImageCount, allImageCount]);

  const leftOffset = firstImageIndex * imageWidth;

  return {
    firstImageIndex,
    setFirstImageIndex,
    shouldShowLeftArrow,
    shouldShowRightArrow,
    leftOffset,
    shiftLeft,
    shiftRight,
  };
};

const HistoryListImpl = ({
  selectedIndex,
  historyList,
  onSelectHistory,
  i18n,
  classes,
}) => {
  const onClick = useCallback((index) => {
    onSelectHistory(historyList[index], index);
  }, [historyList, onSelectHistory]);

  const onKeyDown = useCallback((index, ev) => {
    if (ev.keyCode === 'Enter') {
      onClick(index);
    }
  }, [onClick]);

  const [imagesRef, setImagesRef] = useState(null);
  const {
    shouldShowRightArrow,
    shouldShowLeftArrow,
    shiftRight,
    shiftLeft,
    leftOffset,
  } = useHistoryListState({ imagesRef, allImageCount: historyList.length });

  const leftIconButton = useMemo(() => (
    <IconButton
      className={classNames(classes.paddleBtn, 'paddle-left')}
      onKeyDown={(event) => {
        shiftLeft();
        event.stopPropagation();
        event.preventDefault();
      }}
      onClick={shiftLeft}
      iconProps={{ iconName: 'ChevronLeft' }}
      styles={{
        root: {
          borderRadius: '99px',
          boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
          marginRight: '2px',
        },
      }}
      ariaLabel={i18n.getString(_TL_('click to see previous histories'))}
    />
  ), [classes.paddleBtn, i18n, shiftLeft]);

  const rightIconButton = useMemo(() => (
    <IconButton
      className={classNames(classes.paddleBtnRight, 'paddle-right')}
      onClick={shiftRight}
      iconProps={{ iconName: 'ChevronRight' }}
      styles={{
        root: {
          borderRadius: '99px',
          boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)',
        },
      }}
      ariaLabel={i18n.getString(_TL_('click to see more histories'))}
    />
  ), [classes.paddleBtnRight, i18n, shiftRight]);

  return (
    <div className={classNames('history-list-root', classes.historyListRoot)}>
      <div className="history-list-title-container">
        <Icon iconName="History" className="history-icon" />
        <div className="history-title">{i18n.getString(_TL_('History'))}</div>
      </div>
      <div className="history-list-container">
        {
          shouldShowLeftArrow && (
            <>
              { leftIconButton }
            </>
          )
        }
        <div ref={setImagesRef} className="history-list-images-container">
          <HistoryListImages onClick={onClick} onKeyDown={onKeyDown} historyList={historyList} selectedIndex={selectedIndex} listStyle={{ left: -leftOffset }} />
        </div>
        {
          shouldShowLeftArrow && (
            <>
              <div className={classNames('left-icon-hint')} />
            </>
          )
        }
        {
          shouldShowRightArrow && (
            <>
              <div className={classNames('right-icon-hint')} />
              { rightIconButton }
            </>
          )
        }
      </div>
    </div>
  );
};

HistoryListImpl.propTypes = {
  selectedIndex: PropTypes.number,
  historyList: PropTypes.arrayOf(PropTypes.shape({})),
  onSelectHistory: PropTypes.func,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
};

HistoryListImpl.defaultProps = {
  selectedIndex: -1,
  historyList: [],
  onSelectHistory: () => {},
};

export const GenerationHistoryList = withDefaultStyles(HistoryListImpl, getGenerationHistoryListStyles);
